import React, { ReactNode, useEffect, useState } from 'react';
import toast, { Toast, ToastOptions } from 'react-hot-toast';
import { IApiError } from '../app/api/api.types';
import { formatApiErrorDetails } from './api.utils';
import { is4xxClientError, is5xxServerError, reloadPageNow, SERVER_ERROR_RELOAD_SECONDS } from './httpError.utils';

export const CLIENT_ERROR_TOAST_DURATION_MS = 8 * 1000;
const DEFAULT_TOAST_DURATION_MS = 3 * 1000;
const WARNING_TOAST_DURATION_MS = 5 * 1000;

export interface IFlashMessage {
  type: 'success' | 'warning' | 'error' | 'info';
  message: string;
  title?: string;
  code?: string;
  statusCode?: number;
  error?: Error | IApiError;
  /** When true, shows Refreshing countdown and reloads the page (same UX as 5xx). */
  reloadOnCountdown?: boolean;
}

export function addFlashMessage(message: IFlashMessage) {
  const toastOptions: ToastOptions = {
    duration: DEFAULT_TOAST_DURATION_MS,
  };

  let icon: ReactNode;
  switch (message.type) {
    case 'info':
      icon = <i className="icon icon-info"></i>;
      break;
    case 'success':
      icon = <i className="icon icon-info"></i>;
      break;
    case 'warning':
      icon = <i className="icon icon-info"></i>;
      toastOptions.duration = WARNING_TOAST_DURATION_MS;
      break;
    case 'error':
      icon = <i className="icon icon-info"></i>;
      if (is4xxClientError(message.statusCode)) {
        toastOptions.duration = Infinity;
      } else if (is5xxServerError(message.statusCode)) {
        toastOptions.duration = Infinity;
      } else {
        toastOptions.duration = Infinity;
      }
      break;
    default:
      break;
  }

  toast.custom((t) => <CustomToast message={message} icon={icon} t={t} />, toastOptions);
}

const CustomToast = ({ message, icon, t }: { message: IFlashMessage; icon: ReactNode; t: Toast }) => {
  const [isErrorVisible, setIsErrorVisible] = useState<boolean>(true);
  const [serverErrorCountdown, setServerErrorCountdown] = useState<number>(SERVER_ERROR_RELOAD_SECONDS);
  const is4xxClientErrorToast = is4xxClientError(message.statusCode);
  const is5xx = is5xxServerError(message.statusCode);
  const shouldReload = is5xx || message.reloadOnCountdown === true;
  const showCountdownBar = message.type !== 'error' || is4xxClientErrorToast || shouldReload;
  const countdownBarDurationMs = (() => {
    if (shouldReload) {
      return SERVER_ERROR_RELOAD_SECONDS * 1000;
    }

    if (is4xxClientErrorToast) {
      return CLIENT_ERROR_TOAST_DURATION_MS;
    }

    if (message.type === 'warning') {
      return WARNING_TOAST_DURATION_MS;
    }

    return DEFAULT_TOAST_DURATION_MS;
  })();

  useEffect(() => {
    if (!shouldReload) {
      return undefined;
    }

    setServerErrorCountdown(SERVER_ERROR_RELOAD_SECONDS);

    const countdownTimer = setInterval(() => {
      setServerErrorCountdown((previousSeconds) => Math.max(previousSeconds - 1, 0));
    }, 1000);

    return () => {
      clearInterval(countdownTimer);
    };
  }, [shouldReload]);

  function handleViewErrorClick() {
    setIsErrorVisible(!isErrorVisible);
  }

  function handleCloseClick() {
    toast.dismiss(t.id);
  }

  function handleCountdownEnd() {
    if (shouldReload) {
      reloadPageNow();
      return;
    }

    toast.dismiss(t.id);
  }

  const errorDetails = message.error ? formatApiErrorDetails(message.error) : [];

  return (
    <div className={`flash-message ${message.type}${showCountdownBar ? ' flash-message--auto-dismiss' : ''}`}>
      <button className="close-icon" onClick={handleCloseClick}>
        <i className="icon-close"></i>
      </button>
      {icon}
      <div className="content">
        {message.message && <p className="title">{message.message}</p>}
        {shouldReload && serverErrorCountdown > 0 && (
          <p className="subtitle">Refreshing in {serverErrorCountdown}...</p>
        )}
        {errorDetails.length > 0 && (
          <div className="error">
            <button onClick={handleViewErrorClick}>{isErrorVisible ? 'Hide details' : 'View details'}</button>
            {isErrorVisible && (
              <div className="error-details">
                {errorDetails.length === 1 ? (
                  <p>{errorDetails[0]}</p>
                ) : (
                  <ul>
                    {errorDetails.map((detail, index) => (
                      <li key={index}>{detail}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {showCountdownBar && (
        <div className="countdown-bar">
          <div
            className="countdown-bar__fill"
            style={{ animationDuration: `${countdownBarDurationMs}ms` }}
            onAnimationEnd={handleCountdownEnd}
          />
        </div>
      )}
    </div>
  );
};
